import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/system/Stack';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import EndorsementsApi from '../../../api/EndorsementsApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import {
	AutofillOperations,
	AutofillOperationTypes,
} from '../../../api/IntegrationsApi';
import RenewalsApi from '../../../api/RenewalsApi';
import { AuthData, useAuthContext } from '../../../context/auth';
import { useConfigContext } from '../../../context/config';
import { usePolicyContext } from '../../../context/policy';
import { EndorsementResponse } from '../../../types/endorsements';
import { RenewalCreateRequest, RenewalResponse } from '../../../types/renewals';
import { isAutofillConfigured } from '../../../util/util';
import DropdownButtonItem from '../../DropdownButtonItem';
import {
	DATE_FIELD_VALIDATIONS,
	DATE_PRECISION,
	FORM_MESSAGE,
} from '../../fastForm/data/constants';
import { DateField } from '../../fastForm/fields';
import { dateConversion } from '../../fastForm/utils';
import { errorToast, successToast } from '../../Toast';

const DATE_PICKER_PRECISION = DATE_PRECISION.DAY;

interface FormValues {
	currentEnd: string;
	renewalEndDate: string;
}

interface Props {
	isOpen: boolean;
	loading: boolean;
	currentPolicyEndDate: dayjs.Dayjs;
	authData: AuthData;
	handleClose(): void;
	handleOpen(): void;
	onSubmit(data: FormValues): void;
}

const RenewalCreateModal = (props: Props) => {
	const {
		isOpen,
		handleOpen,
		handleClose,
		loading,
		onSubmit,
		currentPolicyEndDate,
		authData,
	} = props;

	const timezone = authData.auth.timezone;
	const minimumRenewalEndDateInputString =
		dateConversion.dayJsObjectToDateFormString(
			currentPolicyEndDate,
			DATE_PICKER_PRECISION,
		);

	const minimumRenewalEndDateErrorString =
		DATE_FIELD_VALIDATIONS.MIN_DATE_ERROR_MESSAGE +
		minimumRenewalEndDateInputString;

	const { control, handleSubmit } = useForm({
		defaultValues: {
			currentEnd: dateConversion.dayJsObjectToDateFormString(
				currentPolicyEndDate,
				DATE_PICKER_PRECISION,
			),
			renewalEndDate: dateConversion.dayJsObjectToDateFormString(
				currentPolicyEndDate.add(1, 'year'),
				DATE_PICKER_PRECISION,
			),
		},
		resolver: yupResolver(
			yup.object().shape({
				renewalEndDate: yup
					.string()
					.required(FORM_MESSAGE.required)
					.test(
						'is-valid-date',
						DATE_FIELD_VALIDATIONS.INVALID_DATE_ERROR_MESSAGE,
						(value) => {
							if (value === '') {
								return true;
							}
							const date = dateConversion.dateFormStringToDayJs(
								value,
								DATE_PICKER_PRECISION,
							);
							return date?.isValid();
						},
					)
					.test(
						'is-before-minimum-date',
						minimumRenewalEndDateErrorString,
						(value) => {
							const currentDate =
								dateConversion.convertDateStringToZonedTimestamp(
									value,
									DATE_PICKER_PRECISION,
									timezone,
								);
							const minimumDate =
								dateConversion.convertDateStringToZonedTimestamp(
									minimumRenewalEndDateInputString,
									DATE_PICKER_PRECISION,
									timezone,
								);

							return parseInt(currentDate) >= parseInt(minimumDate);
						},
					),
			}),
		),
	});

	const handleModalClose = (
		reason: 'backdropClick' | 'escapeKeyDown',
		callback: () => void,
	) => {
		if (reason !== 'backdropClick') {
			callback();
		}
	};

	return (
		<>
			<DropdownButtonItem onClick={handleOpen} label='Renew Policy' />
			<Dialog
				fullWidth
				open={isOpen}
				onClose={(e, r) => handleModalClose(r, handleClose)}
				aria-labelledby='add-endorsement-modal'
			>
				<Box component='form' onSubmit={handleSubmit(onSubmit)}>
					<DialogTitle>{`Renewal Dates`}</DialogTitle>
					<DialogContent>
						<Stack alignItems='flex-start' direction='column' spacing={2}>
							<DateField<FormValues>
								disableTimePicker
								authData={authData}
								control={control}
								showCalendar
								disabled
								name='currentEnd'
								title='Current Policy End'
								precision={
									DATE_PICKER_PRECISION as 'second' | 'day' | 'month' | 'year'
								}
								type='date'
								values={[]}
								order={0}
							/>
							<DateField<FormValues>
								disableTimePicker
								authData={authData}
								control={control}
								showCalendar
								defaultCalendarMonth={
									currentPolicyEndDate.isValid()
										? currentPolicyEndDate.add(1, 'year')
										: null
								}
								minimum={
									minimumRenewalEndDateErrorString
										? minimumRenewalEndDateErrorString
										: null
								}
								name='renewalEndDate'
								title='Renewal End Date'
								precision={
									DATE_PICKER_PRECISION as 'second' | 'day' | 'month' | 'year'
								}
								type='date'
								values={[]}
								order={0}
							/>
						</Stack>
					</DialogContent>
					<DialogActions sx={{ m: 2 }}>
						<Button
							onClick={handleClose}
							color='neutralButton'
							disabled={loading}
						>
							Close
						</Button>
						<Button
							color='secondary'
							variant='contained'
							type='submit'
							disabled={loading}
						>
							Confirm
						</Button>
					</DialogActions>
				</Box>
			</Dialog>
		</>
	);
};

const RenewalCreateTrigger = () => {
	const authData = useAuthContext();
	const policyData = usePolicyContext();
	const navigate = useNavigate();
	const configData = useConfigContext();
	const timezone = authData.auth.timezone;
	const { policy } = policyData;
	const [loading, setLoading] = useState(false);
	const [isOpen, setIsOpen] = useState(false);
	const hasPermission = authData.hasPermission;
	const handleClose = () => {
		setIsOpen(false);
	};
	const handleOpen = () => setIsOpen(true);

	const { data: renewals } = useQuery({
		queryKey: ['renewals', policy.locator, authData.auth.authorizationToken],
		queryFn: () =>
			RenewalsApi.getRenewalsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
		enabled: hasPermission('renewal.fetch'),
	});

	const { data: endorsements } = useQuery({
		queryKey: [
			'endorsements',
			policy.locator,
			authData.auth.authorizationToken,
		],
		queryFn: () =>
			EndorsementsApi.getEndorsementsForPolicy(
				authData.auth.authorizationToken,
				policy.locator,
			),
		enabled: hasPermission('endorsement.fetch'),
	});

	const acceptedEndorsement = endorsements?.data?.filter(
		(endorsement: EndorsementResponse) => endorsement.state === 'accepted',
	);
	const acceptedRenewal = renewals?.data?.filter(
		(renewal: RenewalResponse) => renewal.state === 'accepted',
	);

	const acceptedRenewalLocator =
		acceptedRenewal?.length && acceptedRenewal[0].locator;

	const acceptedEndorsementLocator =
		acceptedEndorsement?.length && acceptedEndorsement[0].locator;

	const { data: policyWithEndorsement } = useQuery({
		queryKey: [
			'endorsements',
			policy.locator,
			authData.auth.authorizationToken,
			acceptedEndorsementLocator,
		],
		queryFn: () =>
			EndorsementsApi.getPolicyIfEndorsementIssued(
				authData.auth.authorizationToken,
				acceptedEndorsementLocator,
			),
		enabled:
			hasPermission('endorsement.fetch') && !!acceptedEndorsement?.length,
	});

	const { data: policyWithRenewal } = useQuery({
		queryKey: [
			'renewals',
			policy.locator,
			authData.auth.authorizationToken,
			acceptedRenewalLocator,
		],
		queryFn: () =>
			RenewalsApi.getPolicyIfRenewalIssued(
				authData.auth.authorizationToken,
				acceptedRenewalLocator,
			),
		enabled: hasPermission('renewal.fetch') && !!acceptedRenewal?.length,
	});

	const basisPolicy =
		policyWithEndorsement?.data || policyWithRenewal?.data || policy;

	const onSubmit = async (data: FormValues) => {
		setLoading(true);

		const autofillEnabled = isAutofillConfigured(
			basisPolicy.productName,
			AutofillOperations.RENEWAL,
			AutofillOperationTypes.CREATE,
			configData?.dataAutofillSequences ?? [],
		);

		const zonedRenewalEndTimestamp =
			dateConversion.convertDateStringToZonedTimestamp(
				data.renewalEndDate,
				DATE_PICKER_PRECISION,
				timezone,
			);

		const renewalCreateReq: RenewalCreateRequest = {
			renewalEndTimestamp: `${zonedRenewalEndTimestamp}`,
			// default specified autofill sequence will run when autofill array is null
			autofill: autofillEnabled ? null : [],
		};
		try {
			const renewal = await RenewalsApi.createRenewal(
				authData.auth.authorizationToken,
				basisPolicy.locator,
				renewalCreateReq,
			);
			successToast('Successfully created renewal');
			navigate(
				`/policy/${policyData.policy.displayId}/renewals/${renewal.data.locator}`,
			);
			return;
		} catch (error) {
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			errorToast('Failed to create renewal');
		}
		setLoading(false);
	};

	const currPolicyEndDate = basisPolicy.effectiveContractEndTimestamp
		? dateConversion.convertTimestampToDayJS(
				basisPolicy.effectiveContractEndTimestamp,
				timezone,
		  )
		: null;

	return (
		<>
			<RenewalCreateModal
				authData={authData}
				onSubmit={onSubmit}
				loading={loading}
				isOpen={isOpen}
				handleClose={handleClose}
				handleOpen={handleOpen}
				currentPolicyEndDate={currPolicyEndDate}
			/>
		</>
	);
};

export default RenewalCreateTrigger;
